import { useEffect, useState } from "react";
import { MapPin, Pencil, Plus, Trash2, Warehouse as WarehouseIcon } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const emptyForm = { code: "", name: "", address: "" };

export default function Gudang() {
  const [warehouses, setWarehouses] = useState([]);
  const [stockMap, setStockMap] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
    api.get("/reports/stock").then((r) => {
      const map = {};
      r.data.rows.forEach((row) => {
        map[row.warehouse_id] = (map[row.warehouse_id] || 0) + row.qty;
      });
      setStockMap(map);
    }).catch(() => {});
  };

  useEffect(load, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (w) => {
    setEditing(w);
    setForm({ code: w.code, name: w.name, address: w.address });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/warehouses/${editing.id}`, form);
        toast.success("Warehouse updated");
      } else {
        await api.post("/warehouses", form);
        toast.success("Warehouse added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (w) => {
    if (!window.confirm(`Delete warehouse "${w.name}"?`)) return;
    try {
      await api.delete(`/warehouses/${w.id}`);
      toast.success("Warehouse deleted");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="gudang-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-900">Warehouses</h1>
          <p className="mt-1 text-sm text-zinc-500">Manage your warehouse and store locations.</p>
        </div>
        <Button data-testid="btn-add-warehouse" onClick={openCreate} className="rounded-sm bg-blue-600 text-white transition-transform hover:-translate-y-[1px] hover:bg-blue-700">
          <Plus className="mr-2 h-4 w-4" /> Add Warehouse
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {warehouses.length === 0 && (
          <p className="col-span-full rounded-md border border-dashed border-zinc-300 bg-white py-12 text-center text-zinc-400" data-testid="warehouse-empty">
            No warehouses yet
          </p>
        )}
        {warehouses.map((w) => (
          <div key={w.id} className="rounded-md border border-zinc-200 bg-white p-6" data-testid={`warehouse-card-${w.code}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-sm bg-blue-50 text-blue-600">
                  <WarehouseIcon className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-heading text-lg font-bold tracking-tight text-zinc-900">{w.name}</p>
                  <p className="font-mono text-xs text-zinc-500">{w.code}</p>
                </div>
              </div>
              <div className="flex">
                <button data-testid={`btn-edit-warehouse-${w.code}`} onClick={() => openEdit(w)} className="mr-1 rounded-sm p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-blue-600">
                  <Pencil className="h-4 w-4" />
                </button>
                <button data-testid={`btn-delete-warehouse-${w.code}`} onClick={() => remove(w)} className="rounded-sm p-1.5 text-zinc-400 transition-colors hover:bg-red-50 hover:text-red-600">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            {w.address && (
              <p className="mt-4 flex items-start gap-1.5 text-sm text-zinc-500">
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {w.address}
              </p>
            )}
            <div className="mt-4 border-t border-zinc-100 pt-3">
              <p className="text-xs uppercase tracking-wide text-zinc-400">Total Units</p>
              <p className="tnum mt-1 text-xl font-bold text-zinc-900">{stockMap[w.id] || 0}</p>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Warehouse" : "Add Warehouse"}</DialogTitle>
            <DialogDescription className="sr-only">Warehouse details form</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2">
              <Label>Warehouse Code</Label>
              <Input data-testid="warehouse-form-code" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="GD-03" className="rounded-sm border-zinc-300" />
            </div>
            <div className="space-y-2">
              <Label>Warehouse Name</Label>
              <Input data-testid="warehouse-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="East Branch Warehouse" className="rounded-sm border-zinc-300" />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input data-testid="warehouse-form-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Jl. ..." className="rounded-sm border-zinc-300" />
            </div>
            <DialogFooter>
              <Button data-testid="warehouse-form-submit" type="submit" disabled={saving} className="rounded-sm bg-blue-600 text-white hover:bg-blue-700">
                {saving ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
